import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { parse, format, startOfYear } from '@sequencehq/utils/dates'

const formatDateForUrl = (date: Date) => {
  return format(date, 'yyyy-MM-dd')
}

const parseDateFromUrl = (str: string) => {
  return parse(str, 'yyyy-MM-dd', new Date())
}

export const useSelectedDateRange = ({
  defaultDateRange
}: {
  defaultDateRange?: [Date, Date]
} = {}): {
  dateRange: [Date, Date]
  setDateRange: (range: [Date, Date]) => void
} => {
  const [searchParams, setSearchParams] = useSearchParams()

  const startDateFromUrl = searchParams.get('startDate')
  const endDateFromUrl = searchParams.get('endDate')

  const getDateRange = useCallback((): [Date, Date] => {
    if (!startDateFromUrl || !endDateFromUrl) {
      return [startOfYear(new Date()), new Date()]
    }

    return [
      parseDateFromUrl(startDateFromUrl),
      parseDateFromUrl(endDateFromUrl)
    ]
  }, [startDateFromUrl, endDateFromUrl])

  const setDateRange = useCallback(
    (range: [Date, Date]) => {
      setSearchParams(
        params => {
          params.set('startDate', formatDateForUrl(range[0]))
          params.set('endDate', formatDateForUrl(range[1]))
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const dateRange = getDateRange()

  // Check if there is a date range set in search params
  // if not, and default date range is provided, set the search params
  useEffect(() => {
    if (!defaultDateRange) {
      return
    }
    if (!startDateFromUrl || !endDateFromUrl) {
      setDateRange(defaultDateRange as [Date, Date])
    }
  }, [startDateFromUrl, endDateFromUrl, defaultDateRange, setDateRange])

  return {
    dateRange,
    setDateRange
  }
}
